import { createPromise } from './promiseUtils'
import { VIEWER_COMPONENTS_ID_PREFIXES, closest, getElementCompId } from '@wix/thunderbolt-dom-utils'

export function getCompClassType(componentType: string, uiType?: string) {
	return uiType ? `${componentType}_${uiType}` : componentType
}

const comp_type_attribute = 'data-comp-type'

export function getClosestCompIdByHtmlElement(htmlElement: HTMLElement): string {
	for (const prefix of VIEWER_COMPONENTS_ID_PREFIXES) {
		const closestElement = closest(htmlElement, `[id^="${prefix}"]`)
		if (closestElement) {
			return getElementCompId(closestElement)
		}
	}
	return ''
}

export function extractClosestCompTypeFromHtmlElement(htmlElement: HTMLElement): string {
	const closestElement = htmlElement.closest(`[${comp_type_attribute}]`)
	return closestElement?.getAttribute(comp_type_attribute) || ''
}

export type LazyComponentTrigger = 'viewport'
export type LazyComponentOptions = {
	triggers?: Array<LazyComponentTrigger>
	componentResolver: () => Promise<React.ComponentType<any>>
}
export type LazyComponentLoaderResponse = {
	componentPromise: Promise<React.ComponentType<any>>
	clearComponentListeners: () => void
}

export const isForwardRef = (Component: React.ComponentType<any>) =>
	Component.prototype &&
	Component.prototype.hasOwnProperty('$$typeof') &&
	Component.prototype.$$typeof.toString() === 'Symbol(react.forward_ref)'

export const createViewportObserver = (
	compId: string,
	getByCompId: (compId: string, doc?: Document) => HTMLElement | null
) => {
	const targetElement = getByCompId(compId)
	const { promise, resolver } = createPromise()
	if (!targetElement) {
		// If there was a mismatch (or navigation) we want to hydrate immediately and not wait for intersection
		// as the target element might be null and promise will remain hanging
		resolver()
		return {
			promise,
			cleaner: () => {},
			resolver,
		}
	}

	const observer = new IntersectionObserver(
		([entry]) => {
			if (entry.isIntersecting) {
				resolver()
				observer.disconnect()
			}
		},
		{ root: null }
	)
	observer.observe(targetElement)

	return {
		promise,
		cleaner: () => observer.disconnect(),
		// exposed so callers can force the gate open when the observed element is discarded
		resolver,
	}
}

/**
 * The id that comes first in the source structure. A Carmi `getComponentsByType(type).values().get(0)`
 * takes the first match of its flat structure map, which keeps the source insertion order. A VSM
 * `getComponentsByType` walks the page depth-first instead, so when a page has several matches (for
 * example a `HeaderSection` placed right in the page, and another one inside a `RefComponent` that
 * comes earlier in the page's children) the walk can reach a different one first. Ids with no source
 * order (derived ids) keep their walk order, after the original ones.
 */
export const getFirstCompIdInSourceOrder = (
	compIds: Iterable<string>,
	getSourceOrder: (compId: string) => number | undefined
): string | undefined => {
	let firstId: string | undefined
	let firstOrder = Infinity
	for (const compId of compIds) {
		const order = getSourceOrder(compId) ?? Infinity
		if (firstId === undefined || order < firstOrder) {
			firstId = compId
			firstOrder = order
		}
	}
	return firstId
}

export const getHeaderElement = (
	headerCompId: string | null,
	window: Window,
	getByCompId: (compId: string, doc?: Document) => HTMLElement | null
) => {
	let headerElement = getByCompId(headerCompId || 'SITE_HEADER', window.document)
	if (!headerElement) {
		const headerElementsByTags = window.document.getElementsByTagName('header')
		if (headerElementsByTags && headerElementsByTags.length === 1) {
			headerElement = headerElementsByTags[0]
		}
	}

	return headerElement
}
